import { createSlice } from '@reduxjs/toolkit'
import { scroller } from 'react-scroll'
import { LOCATION_CHANGE } from 'connected-react-router'
import { MutationsIndexedByIds, Parcelle } from 'interfaces/Dvf'

export enum Status {
  // Generic
  Idle,
  Pending,
  Succeeded,
  Failed,
  // Custom
  ShowModalBeaucoupDeResultats,
  ShowParcelleResults,
}

export interface DvfState {
  parcelles: Parcelle[]
  mutations: MutationsIndexedByIds
  statusDvf: Status
  errorDvf: string | null
  selectedParcelleIndex: number | null
  statusExportFile: Status
  errorExportFile: string | null
  exportFileBlob: Blob | null
}

const initialState: DvfState = {
  parcelles: [],
  mutations: {},
  statusDvf: Status.Idle,
  errorDvf: null,
  selectedParcelleIndex: null,
  statusExportFile: Status.Idle,
  errorExportFile: null,
  exportFileBlob: null,
}

const name = 'dvf'

const dvfSlice = createSlice({
  name,
  initialState,
  reducers: {
    // Redux Toolkit allows us to write "mutating" logic in reducers. It
    // doesn't actually mutate the state because it uses the Immer library,
    // which detects changes to a "draft state" and produces a brand new
    // immutable state based off those changes
    [LOCATION_CHANGE]: () => initialState,
    idleDvf: () => initialState,
    succeededDvf: (state, action) => {
      const dvf = action.payload

      return {
        ...state,
        statusDvf: Status.Succeeded,
        ...dvf,
      }
    },
    loadingDvf: (state) => ({
      ...state,
      statusDvf: Status.Pending,
    }),
    errorDvf: (state, action) => {
      const error = action.payload

      return {
        ...state,
        errorDvf: error,
        statusDvf: Status.Failed,
      }
    },
    selectParcelle: (state, action) => {
      const uid = action.payload

      const selectedIndex = state.parcelles.findIndex(
        (parcelle) => uid === parcelle.parcelleUid
      )
      return {
        ...state,
        selectedParcelleIndex:
          selectedIndex !== undefined ? selectedIndex : null,
      }
    },
    nextParcelleResult: (state) => {
      if (state.selectedParcelleIndex === null)
        return {
          ...state,
          selectedParcelleIndex: 0,
        }
      if (
        state.selectedParcelleIndex !== null &&
        state.selectedParcelleIndex < state.parcelles.length - 1
      )
        return {
          ...state,
          selectedParcelleIndex: state.selectedParcelleIndex + 1,
        }
      return state
    },
    previousParcelleResult: (state) => {
      if (
        state.selectedParcelleIndex !== null &&
        state.selectedParcelleIndex > 0
      )
        return {
          ...state,
          selectedParcelleIndex: state.selectedParcelleIndex - 1,
        }
      return state
    },
    scrollParcelleToTop: (state) => {
      if (state.selectedParcelleIndex !== null) {
        const { parcelleUid } = state.parcelles[state.selectedParcelleIndex]

        scroller.scrollTo(parcelleUid, {
          duration: 1000,
          delay: 0,
          smooth: true,
          containerId: 'mutation-list-result',
        })
      }
      return state
    },
    idleExportFile: (state) => ({
      ...state,
      exportFileBlob: null,
      statusExportFile: Status.Idle,
      errorExportFile: null,
    }),
    loadingExportFile: (state) => ({
      ...state,
      statusExportFile: Status.Pending,
    }),
    succeededExportFile: (state, action) => {
      const exportFileBlob = action.payload

      return {
        ...state,
        exportFileBlob,
        statusExportFile: Status.Succeeded,
      }
    },
    failedExportFile: (state, action) => {
      const error = action.payload

      return {
        ...state,
        statusExportFile: Status.Failed,
        errorExportFile: error,
      }
    },
    showModalBeaucoupDeResultats: (state) => ({
      ...state,
      statusDvf: Status.ShowModalBeaucoupDeResultats,
    }),
    showParcelleResults: (state) => ({
      ...state,
      statusDvf: Status.ShowParcelleResults,
    }),
  },
})

export const {
  idleDvf,
  succeededDvf,
  loadingDvf,
  errorDvf,
  selectParcelle,
  nextParcelleResult,
  previousParcelleResult,
  scrollParcelleToTop,
  idleExportFile,
  loadingExportFile,
  succeededExportFile,
  failedExportFile,
  showModalBeaucoupDeResultats,
  showParcelleResults,
} = dvfSlice.actions

export default dvfSlice
